import { queryOptions, useQuery } from '@tanstack/react-query';
import type { CdpOutIntegration, IntegrationsGetSegmentOutLegacyResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { integrationKeys } from './entity';

/**
 * Single-object integration status reads (mParticle, CDP out, Segment in/out,
 * data-out). The "not configured" case is a fetch error that resolves to
 * `null`/`false` rather than surfacing, so the directory shows the integration
 * as simply not-enabled.
 */

const STALE_TIME = 60_000;

const statusKey = (workspaceId: number, name: string) => [...integrationKeys.all(workspaceId), 'status', name] as const;

/**
 * mParticle status re-exported from the `mparticle-integration` entity.
 *
 * This used to be a standalone read on a `status` sub-key that swallowed every
 * error to `null`. It now delegates so the connector's config page and these
 * fan-outs share one cache slot: connecting, saving or disconnecting updates the
 * directory's "connected" state and the connections overview with no refetch,
 * the way mutating the Ember record updated the catalog row it was loaded from.
 *
 * These are the non-retrying options: a fan-out folds this record's `isLoading`
 * into a page-level loader, so a 5xx must fail fast rather than hold the page
 * through the default backoff. See `getMParticleIntegrationStatusQueryOptions`.
 */
export {
  getMParticleIntegration as getMparticleIntegration,
  getMParticleIntegrationStatusQueryOptions as getMparticleIntegrationQueryOptions,
} from '../mparticle-integration/queries';

/* --------------------------------- CDP out ------------------------------- */

/** @public Whether a CDP-out integration is configured (drives CDP in + out). */
export async function getCdpOutEnabled(workspaceId: number): Promise<boolean> {
  try {
    const data = await hydraUiApi.integrations.getCdpOut({
      params: { path: { environment_id: String(workspaceId) } },
    });
    const cdpOut: CdpOutIntegration | undefined = data.cdp_out;
    return cdpOut != null;
  } catch {
    return false;
  }
}

export const getCdpOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'cdp_out'),
    queryFn: () => getCdpOutEnabled(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useCdpOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getCdpOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getCdpOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------------- Segment in ------------------------------ */

/** @public Whether the Segment (Destination) integration is present. */
export async function getSegmentInPresent(workspaceId: number): Promise<boolean> {
  try {
    await hydraUiApi.integrations.getSegmentIn({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return true;
  } catch {
    return false;
  }
}

export const getSegmentInPresentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segment_in'),
    queryFn: () => getSegmentInPresent(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useSegmentInPresent = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentInPresentQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentInPresentQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------- Segment out (segdotcom) ----------------------- */

/**
 * @public The full Segment-out (segdotcom) status, or `null` when not configured.
 *
 * Returned as the API sends it. Unlike its `segment_out` sibling this response is
 * not enveloped — the fields sit at the top level.
 */
export async function getSegmentOut(workspaceId: number): Promise<IntegrationsGetSegmentOutLegacyResponse | null> {
  try {
    return await hydraUiApi.integrations.getSegdotcom({
      params: { path: { environment_id: String(workspaceId) } },
    });
  } catch {
    return null;
  }
}

export const getSegmentOutQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segdotcom'),
    queryFn: () => getSegmentOut(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public Whether the Segment (Source) integration is enabled — selected off the
 * full read so the directory gate and the connections overview share one fetch.
 */
export const getSegmentOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    ...getSegmentOutQueryOptions(workspaceId),
    select: (data: IntegrationsGetSegmentOutLegacyResponse | null) => data?.enabled ?? false,
  });

/** @public */
export const useSegmentOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* -------------------------------- Data out ------------------------------- */

/**
 * Data-out status re-exported from the `data-out-integration` entity.
 *
 * These used to be a standalone read on a `status` sub-key. They now delegate so
 * the connector's config page and these fan-outs share one cache slot: saving or
 * disconnecting a connector updates the directory's "connected" state and the
 * connections overview with no refetch, the way mutating the Ember record
 * updated the catalog row it was loaded from.
 *
 * These are the non-retrying options: a fan-out folds this record's `isLoading`
 * into a page-level loader, so a 5xx must fail fast rather than hold the page
 * through the default backoff. See `getDataOutIntegrationStatusQueryOptions`.
 */
export {
  getDataOutIntegration as getDataOut,
  getDataOutIntegrationStatusQueryOptions as getDataOutQueryOptions,
  getDataOutIntegrationEnabledQueryOptions as getDataOutEnabledQueryOptions,
} from '../data-out-integration/queries';
